import { useEffect, useRef, useState } from "react";
import { VoiceBenchSession, type Turn } from "../../services/voiceBench";
import { InterruptionChip } from "../InterruptionChip";

// The voice bench runs the full v3 pipeline against the SAVED companion. `savedId` is
// non-null only when the current guide exists on the server.
export function VoiceBench({ savedId }: { savedId: string | null }) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [sys, setSys] = useState<string[]>([]);
  const [status, setStatus] = useState("idle");
  const [live, setLive] = useState(false);
  const sessionRef = useRef<VoiceBenchSession | null>(null);
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => sessionRef.current?.stop();
  }, []);

  useEffect(() => {
    logRef.current?.scrollTo(0, logRef.current.scrollHeight);
  }, [turns, sys]);

  async function start() {
    if (!savedId) return;
    setTurns([]);
    setSys([]);
    const session = new VoiceBenchSession(savedId, {
      onTurns: setTurns,
      onStatus: (text, isLive) => {
        setStatus(text);
        setLive(isLive);
      },
      onSystem: (text) => setSys((s) => [...s, text]),
    });
    sessionRef.current = session;
    try {
      await session.start();
    } catch (e) {
      setStatus(`mic/audio failed: ${e instanceof Error ? e.message : e}`);
      setLive(false);
    }
  }

  function stop() {
    sessionRef.current?.stop();
    sessionRef.current = null;
  }

  // Interleave turns and system lines is overkill; show turns then system tail.
  return (
    <div className="bench">
      <div className="voice-bar">
        {!live ? (
          <button className="btn btn-good" onClick={start} disabled={!savedId}>🎙 Start voice session</button>
        ) : (
          <button className="btn btn-danger" onClick={stop}>■ Stop voice</button>
        )}
        <span className={`voice-status${live ? " live" : ""}`}>voice: {status}</span>
      </div>
      <div className="chat-log" ref={logRef}>
        {!savedId && (
          <div className="msg sys">Save the companion first — the voice bench runs against the saved version.</div>
        )}
        {turns.map((t) => (
          <div key={t.id} style={{ display: "contents" }}>
            {t.user && <div className="msg user">{t.user}</div>}
            {(t.guide || t.interruption) && (
              <div className="msg guide">
                {t.guide || (t.interruption ? "[interrupted before audio reached caller]" : "")}
                {t.interruption && (
                  <InterruptionChip
                    info={{
                      heardRatio: t.interruption.heardRatio,
                      intendedText: t.interruption.intendedText,
                      spokenText: t.interruption.spokenText || t.guide,
                      interruptedAtMs: t.interruption.interruptedAtMs,
                    }}
                  />
                )}
              </div>
            )}
          </div>
        ))}
        {sys.map((s, i) => (
          <div key={`sys-${i}`} className="msg sys">{s}</div>
        ))}
      </div>
    </div>
  );
}
